/* Hallmark · component-scope redesign: auth card · genre: editorial · theme: host_ui.css (design.md tokens)
 * pre-emit critique: P5 H5 E5 S5 R5 V4
 */
import React from "react";
import { useContent } from "@thoughtbot/superglue";

import {
  Form,
  type FormProps,
  type RailsEmailFieldProps,
  type RailsPasswordFieldProps,
  type RailsSubmitButtonProps,
} from "@javascript/components/Inputs";
import { AuthCard } from "@javascript/components/AuthCard";
import { AuthConsentNotice } from "@javascript/components/AuthConsentNotice";
import { AuthShell } from "@javascript/components/AuthShell";
import { GoogleAuthButton } from "@javascript/components/GoogleAuthButton";
import { Button } from "@javascript/components/ui/button";
import { Field, FieldLabel } from "@javascript/components/ui/field";
import { Input } from "@javascript/components/ui/input";
import { TypographyH1, TypographyMuted } from "@javascript/components/ui/typography";

type SignInContent = {
  signInForm: FormProps<{
    email: RailsEmailFieldProps;
    password: RailsPasswordFieldProps;
    submit: RailsSubmitButtonProps;
  }>;
  signUpPath: string;
  confirmationPath: string;
  passwordResetPath: string;
  brandMotifPath: string;
  googleAuthPath?: string;
  alert?: string;
};

const secondaryLinkClass =
  "inline-flex min-h-11 items-center whitespace-nowrap text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring";

export default function SignIn() {
  const { signInForm, signUpPath, confirmationPath, passwordResetPath, brandMotifPath, googleAuthPath, alert } =
    useContent<SignInContent>();
  const { form, extras, inputs } = signInForm;

  return (
    <AuthShell brandMotifPath={brandMotifPath}>
      <AuthCard>
        <div className="space-y-2">
          <TypographyH1 className="text-left">Sign in</TypographyH1>
          <TypographyMuted>Continue creating your wedding invitation.</TypographyMuted>
        </div>

        {alert && (
          <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive" role="alert">
            {alert}
          </div>
        )}

        {googleAuthPath && (
          <>
            <div className="mt-6">
              <GoogleAuthButton authPath={googleAuthPath} />
            </div>

            <div className="my-6 flex items-center gap-4">
              <div className="flex-1 border-t border-border"></div>
              <span className="text-sm text-muted-foreground">Or</span>
              <div className="flex-1 border-t border-border"></div>
            </div>
          </>
        )}

        {/* No data-sg-visit: sign-in changes currentUser identity, so the whole app needs a full reload to flush stale in-memory Superglue state. */}
        <Form {...form} extras={extras} className="mt-6 space-y-5">
          <Field>
            <FieldLabel htmlFor={inputs.email.id}>Email</FieldLabel>
            <Input {...inputs.email} placeholder="you@example.com" />
          </Field>
          <Field>
            <FieldLabel htmlFor={inputs.password.id}>Password</FieldLabel>
            <Input {...inputs.password} />
          </Field>
          <Button type="submit" className="h-12 w-full rounded-full text-base font-semibold shadow-sm">
            {inputs.submit.text}
          </Button>
        </Form>
      </AuthCard>

      {/* Outside the card on purpose: the card is the choice between Google and
          email, and these speak about the account around that choice. */}
      <div className="mt-5">
        <nav aria-label="Account help" data-auth-secondary-actions>
          <ul className="flex flex-wrap gap-x-6 text-sm">
            <li>
              <a href={passwordResetPath} data-sg-visit className={secondaryLinkClass}>
                Forgot password?
              </a>
            </li>
            <li>
              <a href={confirmationPath} data-sg-visit className={secondaryLinkClass}>
                Resend confirmation
              </a>
            </li>
          </ul>
        </nav>

        <p
          data-auth-signup-prompt
          className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm"
        >
          <span className="text-muted-foreground">New to Goldypaper?</span>
          <a
            href={signUpPath}
            className="inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap font-semibold text-foreground underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
          >
            Create an account
            <span aria-hidden className="text-primary">
              →
            </span>
          </a>
        </p>

        <div className="mt-4">
          <AuthConsentNotice />
        </div>
      </div>
    </AuthShell>
  );
}
